/**
 * Agent Pages -- Webhooks
 *
 * Manage webhook subscriptions and view delivery logs for the current agent.
 * Redesigned with stat cards, dropdown actions, payload inspector, and test delivery.
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Plus, Trash2, RotateCw, Copy, Check, AlertTriangle, Loader2,
  Inbox, Webhook, MoreHorizontal, Zap, Search, Power, PowerOff, Eye,
} from 'lucide-react'
import { useAgentContext } from '../../../components/layout/AgentContextLayout'
import { webhooksService, SUPPORTED_EVENTS } from '../../../services/webhooks'
import type {
  Webhook as WebhookType, WebhookCreateRequest, DeliveryLog, WebhookStats,
} from '../../../services/webhooks'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Badge } from '../../../components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/Card'
import { Checkbox } from '../../../components/ui/checkbox'
import { Progress } from '../../../components/ui/progress'
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../../components/ui/table'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '../../../components/ui/alert-dialog'
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../../components/ui/sheet'
import {
  SelectRoot, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/Select'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '../../../components/ui/ShadcnDropdownMenu'
import { ScrollArea } from '../../../components/ui/scroll-area'
import { useToast } from '../../../hooks/useToast'

export const WebhooksPage: React.FC = () => {
  const { chatbot } = useAgentContext()
  const { toast } = useToast()

  // Data state
  const [webhooks, setWebhooks] = useState<WebhookType[]>([])
  const [logs, setLogs] = useState<DeliveryLog[]>([])
  const [stats, setStats] = useState<WebhookStats | null>(null)

  // UI state
  const [isLoading, setIsLoading] = useState(true)
  const [logsLoading, setLogsLoading] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [creating, setCreating] = useState(false)
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const [confirmRetryId, setConfirmRetryId] = useState<string | null>(null)
  const [filterText, setFilterText] = useState('')

  // Create form
  const [newUrl, setNewUrl] = useState('')
  const [selectedEvents, setSelectedEvents] = useState<string[]>(['*'])

  // Secret display (shown once after create)
  const [createdSecret, setCreatedSecret] = useState<string | null>(null)
  const [copiedSecret, setCopiedSecret] = useState(false)

  // Test dialog
  const [showTestDialog, setShowTestDialog] = useState(false)
  const [testWebhookId, setTestWebhookId] = useState('')
  const [testEventType, setTestEventType] = useState('')
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; http_status: number | null; error_message: string | null } | null>(null)

  // Payload inspector
  const [inspectorLog, setInspectorLog] = useState<DeliveryLog | null>(null)

  // Delete confirm (for dropdown)
  const [deleteWebhookId, setDeleteWebhookId] = useState<string | null>(null)

  const loadStats = useCallback(async () => {
    try {
      const data = await webhooksService.stats(chatbot.id)
      setStats(data)
    } catch {
      // Stats are non-critical
    }
  }, [chatbot.id])

  const loadLogs = useCallback(async () => {
    try {
      setLogsLoading(true)
      const data = await webhooksService.logs(chatbot.id)
      setLogs(data)
    } catch {
      // Logs are non-critical
    } finally {
      setLogsLoading(false)
    }
  }, [chatbot.id])

  // Initial load
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const [wh, lg, st] = await Promise.all([
          webhooksService.list(chatbot.id),
          webhooksService.logs(chatbot.id),
          webhooksService.stats(chatbot.id).catch(() => null),
        ])
        if (!cancelled) {
          setWebhooks(wh)
          setLogs(lg)
          if (st) setStats(st)
        }
      } catch {
        if (!cancelled) {
          toast({ title: 'Error', description: 'Failed to load webhooks', variant: 'destructive' })
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbot.id])

  // Event toggle
  function toggleEvent(event: string) {
    if (event === '*') {
      setSelectedEvents(prev => prev.includes('*') ? [] : ['*'])
      return
    }
    setSelectedEvents(prev => {
      const without = prev.filter(e => e !== '*')
      if (without.includes(event)) {
        return without.filter(e => e !== event)
      }
      return [...without, event]
    })
  }

  const allSelected = selectedEvents.includes('*')

  // Create webhook
  async function handleCreate() {
    if (!newUrl.trim()) {
      toast({ title: 'Error', description: 'URL is required', variant: 'destructive' })
      return
    }
    if (selectedEvents.length === 0) {
      toast({ title: 'Error', description: 'Select at least one event', variant: 'destructive' })
      return
    }

    try {
      setCreating(true)
      const body: WebhookCreateRequest = {
        url: newUrl.trim(),
        events: selectedEvents,
      }
      const result = await webhooksService.create(chatbot.id, body)
      setCreatedSecret(result.secret)
      setWebhooks(prev => [...prev, result])
      setNewUrl('')
      setSelectedEvents(['*'])
      loadStats()
      toast({ title: 'Webhook created', description: 'Store the signing secret — it won\'t be shown again.' })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to create webhook'
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    } finally {
      setCreating(false)
    }
  }

  // Toggle enable/disable
  async function handleToggle(webhookId: string, enabled: boolean) {
    try {
      const updated = await webhooksService.toggle(webhookId, enabled)
      setWebhooks(prev => prev.map(w => w.id === webhookId ? { ...w, enabled: updated.enabled, failure_count: updated.failure_count } : w))
      toast({ title: enabled ? 'Webhook enabled' : 'Webhook disabled' })
    } catch {
      toast({ title: 'Error', description: 'Failed to update webhook', variant: 'destructive' })
    }
  }

  // Delete webhook
  async function handleDelete(webhookId: string) {
    try {
      await webhooksService.remove(webhookId)
      setWebhooks(prev => prev.filter(w => w.id !== webhookId))
      setDeleteWebhookId(null)
      loadStats()
      toast({ title: 'Webhook deleted' })
    } catch {
      toast({ title: 'Error', description: 'Failed to delete webhook', variant: 'destructive' })
    }
  }

  // Retry delivery
  async function handleRetry(logId: string) {
    try {
      setRetryingId(logId)
      const result = await webhooksService.retry(logId)
      if (result.success) {
        toast({ title: 'Delivery successful', description: `Attempt #${result.attempt ?? '?'} — HTTP ${result.http_status}` })
      } else {
        toast({ title: 'Delivery failed', description: `Attempt #${result.attempt ?? '?'} — ${result.error_message || 'Unknown error'}`, variant: 'destructive' })
      }
      loadLogs()
      loadStats()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to retry delivery'
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    } finally {
      setRetryingId(null)
    }
  }

  // Test webhook
  async function handleTest() {
    if (!testWebhookId || !testEventType) {
      toast({ title: 'Error', description: 'Select a webhook and event type', variant: 'destructive' })
      return
    }
    try {
      setTestLoading(true)
      setTestResult(null)
      const result = await webhooksService.test(testWebhookId, { event_type: testEventType })
      setTestResult({ success: result.success, http_status: result.http_status, error_message: result.error_message })
      loadLogs()
      loadStats()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to send test'
      setTestResult({ success: false, http_status: null, error_message: msg })
    } finally {
      setTestLoading(false)
    }
  }

  // Open test dialog from dropdown
  function openTestForWebhook(webhookId: string) {
    setTestWebhookId(webhookId)
    setTestEventType('')
    setTestResult(null)
    setShowTestDialog(true)
  }

  // Copy secret
  function copySecret() {
    if (!createdSecret) return
    navigator.clipboard.writeText(createdSecret)
    setCopiedSecret(true)
    setTimeout(() => setCopiedSecret(false), 2000)
  }

  // Close create dialog
  function closeCreateDialog() {
    setShowCreateDialog(false)
    setCreatedSecret(null)
    setCopiedSecret(false)
    setNewUrl('')
    setSelectedEvents(['*'])
  }

  function formatDate(iso: string | null) {
    if (!iso) return '—'
    return new Date(iso).toLocaleString()
  }

  // Filtered webhooks
  const filteredWebhooks = filterText
    ? webhooks.filter(w => w.url.toLowerCase().includes(filterText.toLowerCase()))
    : webhooks

  // Loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Webhooks</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Receive real-time event notifications via HTTP callbacks.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Webhooks</CardDescription>
            <CardTitle className="text-2xl">
              {stats?.total_webhooks ?? webhooks.length} / {stats?.max_webhooks ?? 10}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Progress
              value={((stats?.total_webhooks ?? webhooks.length) / (stats?.max_webhooks ?? 10)) * 100}
              className="h-2"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Successful Deliveries</CardDescription>
            <CardTitle className="text-2xl">{stats?.successful_deliveries ?? 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant="success" size="sm">Delivered</Badge>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Failed Deliveries</CardDescription>
            <CardTitle className="text-2xl">{stats?.failed_deliveries ?? 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge
              variant={(stats?.failed_deliveries ?? 0) > 0 ? 'error' : 'success'}
              size="sm"
            >
              {(stats?.failed_deliveries ?? 0) > 0 ? 'Has failures' : 'All clear'}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by URL..."
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setTestWebhookId(webhooks[0]?.id || '')
              setTestEventType('')
              setTestResult(null)
              setShowTestDialog(true)
            }}
            disabled={webhooks.length === 0}
          >
            <Zap className="h-4 w-4 mr-1" />
            Send Test
          </Button>
          <Button
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            disabled={webhooks.length >= 10}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Webhook
          </Button>
        </div>
      </div>

      {/* Webhooks Table */}
      {filteredWebhooks.length === 0 && webhooks.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-full bg-muted flex items-center justify-center">
              <Webhook className="h-7 w-7 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No webhooks yet</p>
            <p className="text-xs text-muted-foreground max-w-xs mx-auto">
              Add a webhook to receive event notifications when conversations, handoffs, or training events occur.
            </p>
          </CardContent>
        </Card>
      ) : filteredWebhooks.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">No webhooks match your filter.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Failures</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWebhooks.map(wh => (
                    <TableRow key={wh.id}>
                      <TableCell className="font-mono text-xs max-w-[260px] truncate" title={wh.url}>
                        {wh.url}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {wh.events.includes('*') ? (
                            <Badge variant="secondary" size="sm">All events</Badge>
                          ) : (
                            wh.events.slice(0, 3).map(ev => (
                              <Badge key={ev} variant="outline" size="sm">{ev}</Badge>
                            ))
                          )}
                          {!wh.events.includes('*') && wh.events.length > 3 && (
                            <Badge variant="outline" size="sm">+{wh.events.length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={wh.enabled ? 'success' : 'secondary'} size="sm">
                          {wh.enabled ? 'Active' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {wh.failure_count > 0 ? (
                          <span className="text-destructive font-medium">{wh.failure_count} / 10</span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDate(wh.created_at)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleToggle(wh.id, !wh.enabled)}>
                              {wh.enabled ? (
                                <><PowerOff className="h-4 w-4 mr-2" /> Disable</>
                              ) : (
                                <><Power className="h-4 w-4 mr-2" /> Enable</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openTestForWebhook(wh.id)}>
                              <Zap className="h-4 w-4 mr-2" /> Send Test Event
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setDeleteWebhookId(wh.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delivery Logs Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Delivery Logs</h2>
            <p className="text-sm text-muted-foreground">Last 100 delivery attempts</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadLogs} disabled={logsLoading}>
            <RotateCw className={`h-4 w-4 mr-1 ${logsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {logsLoading && logs.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center space-y-3">
              <div className="mx-auto w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                <Inbox className="h-7 w-7 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">No delivery logs</p>
              <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                Logs will appear here once webhooks start receiving events.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>HTTP Code</TableHead>
                      <TableHead>Attempt</TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead className="w-20" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map(log => (
                      <TableRow
                        key={log.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setInspectorLog(log)}
                      >
                        <TableCell>
                          <Badge variant="outline" size="sm">{log.event_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={log.status === 'success' ? 'success' : log.status === 'failed' ? 'error' : 'warning'}
                            size="sm"
                          >
                            {log.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {log.http_status ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {log.attempt}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDate(log.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setInspectorLog(log) }}
                              title="View payload"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setConfirmRetryId(log.id) }}
                              disabled={retryingId === log.id}
                              title={log.status === 'success' ? 'Re-send this delivery' : (log.error_message || 'Retry this delivery')}
                            >
                              <RotateCw className={`h-4 w-4 ${retryingId === log.id ? 'animate-spin' : ''}`} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Payload Inspector Sheet */}
      <Sheet open={!!inspectorLog} onOpenChange={(open) => { if (!open) setInspectorLog(null) }}>
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Delivery Details</SheetTitle>
            <SheetDescription>
              Inspect the webhook delivery attempt and payload.
            </SheetDescription>
          </SheetHeader>
          {inspectorLog && (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <div className="mt-1">
                    <Badge
                      variant={inspectorLog.status === 'success' ? 'success' : inspectorLog.status === 'failed' ? 'error' : 'warning'}
                      size="sm"
                    >
                      {inspectorLog.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">HTTP Status</span>
                  <p className="mt-1 font-mono">{inspectorLog.http_status ?? '—'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Event Type</span>
                  <p className="mt-1 font-mono text-xs">{inspectorLog.event_type}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Attempt</span>
                  <p className="mt-1">{inspectorLog.attempt}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Webhook ID</span>
                  <p className="mt-1 font-mono text-xs truncate">{inspectorLog.webhook_id}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">Timestamp</span>
                  <p className="mt-1 text-xs">{formatDate(inspectorLog.created_at)}</p>
                </div>
              </div>

              {inspectorLog.error_message && (
                <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
                  <p className="text-sm font-medium text-destructive">Error</p>
                  <p className="text-xs text-destructive/80 mt-1">{inspectorLog.error_message}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-medium mb-2">Payload Preview</p>
                <ScrollArea className="h-[300px] rounded-md border">
                  <pre className="p-4 text-xs font-mono whitespace-pre-wrap break-all">
                    {inspectorLog.payload_preview
                      ? (() => {
                          try {
                            return JSON.stringify(JSON.parse(inspectorLog.payload_preview), null, 2)
                          } catch {
                            return inspectorLog.payload_preview
                          }
                        })()
                      : 'No payload data available'}
                  </pre>
                </ScrollArea>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Send Test Dialog */}
      <Dialog open={showTestDialog} onOpenChange={(open) => { if (!open) { setShowTestDialog(false); setTestResult(null) } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Test Event</DialogTitle>
            <DialogDescription>
              Send a test event to verify your webhook endpoint is receiving events correctly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Webhook</label>
              <SelectRoot value={testWebhookId} onValueChange={setTestWebhookId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a webhook" />
                </SelectTrigger>
                <SelectContent>
                  {webhooks.map(wh => (
                    <SelectItem key={wh.id} value={wh.id}>
                      {wh.url.length > 40 ? wh.url.substring(0, 40) + '...' : wh.url}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Event Type</label>
              <SelectRoot value={testEventType} onValueChange={setTestEventType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an event type" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_EVENTS.map(ev => (
                    <SelectItem key={ev} value={ev}>{ev}</SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>

            {testResult && (
              <div className={`p-3 rounded-md border ${testResult.success
                ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800'
              }`}>
                <div className="flex items-center gap-2">
                  {testResult.success ? (
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  )}
                  <p className={`text-sm font-medium ${testResult.success
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                  }`}>
                    {testResult.success ? 'Test delivered successfully' : 'Test delivery failed'}
                  </p>
                </div>
                {testResult.http_status && (
                  <p className="text-xs mt-1 text-muted-foreground">
                    HTTP {testResult.http_status}
                  </p>
                )}
                {testResult.error_message && (
                  <p className="text-xs mt-1 text-muted-foreground">{testResult.error_message}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTestDialog(false); setTestResult(null) }}>
              Close
            </Button>
            <Button onClick={handleTest} disabled={testLoading || !testWebhookId || !testEventType}>
              {testLoading ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending...</>
              ) : (
                <><Zap className="h-4 w-4 mr-1" /> Send Test</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Webhook Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) closeCreateDialog() }}>
        <DialogContent className="sm:max-w-md">
          {createdSecret ? (
            <>
              <DialogHeader>
                <DialogTitle>Webhook Created</DialogTitle>
                <DialogDescription>
                  Store this signing secret securely. It will not be shown again.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    This secret is shown only once. Copy and store it now.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 rounded bg-muted text-xs font-mono break-all select-all">
                    {createdSecret}
                  </code>
                  <Button variant="outline" size="sm" onClick={copySecret}>
                    {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={closeCreateDialog}>Done</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Add Webhook</DialogTitle>
                <DialogDescription>
                  Subscribe a URL to receive event notifications for this agent.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Endpoint URL</label>
                  <Input
                    placeholder="https://example.com/webhooks"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Events</label>
                  <div className="space-y-2 max-h-52 overflow-y-auto p-3 border rounded-md">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={() => toggleEvent('*')}
                      />
                      <span className="text-sm font-medium">All events</span>
                    </label>
                    <div className="border-t my-2" />
                    {SUPPORTED_EVENTS.map(event => (
                      <label key={event} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={allSelected || selectedEvents.includes(event)}
                          disabled={allSelected}
                          onCheckedChange={() => toggleEvent(event)}
                        />
                        <span className="text-sm font-mono">{event}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeCreateDialog}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating}>
                  {creating ? 'Creating...' : 'Create Webhook'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteWebhookId} onOpenChange={(open) => { if (!open) setDeleteWebhookId(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete webhook?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the webhook subscription. Events will no longer be delivered to this URL.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteWebhookId && handleDelete(deleteWebhookId)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Retry Confirmation Dialog */}
      <AlertDialog open={!!confirmRetryId} onOpenChange={(open) => { if (!open) setConfirmRetryId(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-send this delivery?</AlertDialogTitle>
            <AlertDialogDescription>
              This will re-enqueue the webhook delivery and send it again to the endpoint.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirmRetryId) { handleRetry(confirmRetryId); setConfirmRetryId(null) } }}>
              Re-send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
